/**
 * Pure helpers for the postcode a customer typed in the store search: deciding whether the term is
 * a postcode, prefilling it into the personal-form zip, and telling whether the zip still holds it.
 */

/**
 * Normalises a postcode the same way the zip input does on blur
 * (`trim().toUpperCase().replace(/\s+/g, ' ')`). Comparisons must use this, because the blur
 * normalisation would otherwise break an exact-string match.
 */
export function normalizePostcode(value: unknown): string {
  return String(value ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
}

/** A term that is a postcode and nothing else: 3–10 chars of letters/digits/space/hyphen, at least one
 *  digit, and no run of three or more letters (so "3011 Bern", "6300 Zug", "Hofweg 12" are rejected while
 *  "1003", "80331", "1234 AB", "12-345", "123 45", "SW1A 1AA" are accepted). */
export function isPlainPostcode(term: string | null | undefined): boolean {
  const t = (term ?? '').trim();
  return /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9 -]{2,9}$/.test(t) && !/[A-Za-z]{3,}/.test(t);
}

/**
 * True when the zip field still holds the store-search postcode. An empty field never does, and
 * neither does a field without a store-search postcode.
 */
export function isZipPrefilledFromStorePostcode(zip: unknown, storePostcode: unknown): boolean {
  const normalizedZip = normalizePostcode(zip);
  const normalizedStorePostcode = normalizePostcode(storePostcode);
  return normalizedZip !== '' && normalizedStorePostcode !== '' && normalizedZip === normalizedStorePostcode;
}

export interface PersonalFormZipInput {
  /** The zip currently in the form state (URL customer, restored customer or a typed value). */
  currentZip: string | null | undefined;
  /** The zip passed explicitly as customer detail by the integrator. */
  explicitCustomerZip: string | null | undefined;
  /** The postcode the customer typed in the store search, if any. */
  storePostcode: string | null | undefined;
  /** The normalised store-search postcode this widget last wrote into the zip, if any. */
  filledFromPostcode: string | null;
}

export interface PersonalFormZipResolution {
  zip: string | null | undefined;
  /** The normalised store-search postcode now held by the zip because this widget filled it. */
  filledFromPostcode: string | null;
}

/**
 * The single precedence rule for the personal-form zip:
 * 1. an explicit customer zip wins; the zip is left as it is so the customer prefill can fill it,
 * 2. with a store-search postcode: an empty zip, or a zip this widget filled itself, gets the
 *    store-search postcode; any other zip (URL, restored or typed by the customer) is kept,
 * 3. without a store-search postcode: a zip this widget filled is emptied (the search that filled
 *    it is gone); any other zip is kept.
 * Whenever the zip is kept, it no longer holds what this widget filled, so the remembered fill is
 * cleared; otherwise a later hand-typed zip equal to that old fill would be treated as our own.
 */
export function resolvePersonalFormZip({
  currentZip,
  explicitCustomerZip,
  storePostcode,
  filledFromPostcode,
}: PersonalFormZipInput): PersonalFormZipResolution {
  if (normalizePostcode(explicitCustomerZip) !== '') {
    return { zip: currentZip, filledFromPostcode: null };
  }

  const normalizedZip = normalizePostcode(currentZip);
  const normalizedStorePostcode = normalizePostcode(storePostcode);

  if (normalizedStorePostcode !== '') {
    if (normalizedZip === '' || normalizedZip === filledFromPostcode) {
      return { zip: storePostcode, filledFromPostcode: normalizedStorePostcode };
    }
    return { zip: currentZip, filledFromPostcode: null };
  }

  if (normalizedZip !== '' && normalizedZip === filledFromPostcode) {
    return { zip: null, filledFromPostcode: null };
  }

  return { zip: currentZip, filledFromPostcode: null };
}
